/**
 * 角色相关的类型定义和常量
 */

// 预定义角色常量
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  EMPLOYEE: 'employee'
} as const;

// 角色类型
export type Role = typeof ROLES[keyof typeof ROLES];

// 角色显示名称映射
export const ROLE_DISPLAY_NAMES: Record<Role, string> = {
  [ROLES.SUPER_ADMIN]: '超级管理员',
  [ROLES.ADMIN]: '管理员',
  [ROLES.MANAGER]: '经理',
  [ROLES.EMPLOYEE]: '员工'
};

// 角色描述映射
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  [ROLES.SUPER_ADMIN]: '系统最高权限',
  [ROLES.ADMIN]: '用户管理权限',
  [ROLES.MANAGER]: '部门管理权限',
  [ROLES.EMPLOYEE]: '基础使用权限'
};

// 角色信息接口
export interface RoleInfo {
  name: Role;
  displayName: string;
  description: string;
}

// 完整的角色数据结构
export interface RoleData {
  id: string;
  name: string;
  displayName: string;
  description: string;
  isSystem: boolean; // 是否为系统预定义角色
  active: boolean;
  created: Date;
  lastModified: Date;
}

// 创建角色输入
export interface CreateRoleInput {
  name: string;
  displayName: string;
  description: string;
}

// 更新角色输入
export interface UpdateRoleInput {
  displayName?: string;
  description?: string;
  active?: boolean;
}

// 角色查询参数
export interface RoleQueryParams {
  page?: number;
  limit?: number;
  active?: boolean;
  name?: string;
  isSystem?: boolean;
}

// 角色查询结果
export interface RoleQueryResult {
  roles: RoleData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// 用户角色操作输入
export interface UserRoleInput {
  roles: string[];
}

// 角色工具类
export class RoleUtils {
  /**
   * 获取所有预定义角色
   */
  static getAllRoles(): RoleInfo[] {
    return Object.values(ROLES).map(role => ({
      name: role,
      displayName: ROLE_DISPLAY_NAMES[role],
      description: ROLE_DESCRIPTIONS[role]
    }));
  }

  /**
   * 获取角色显示名称
   */
  static getRoleDisplayName(roleName: string): string {
    const role = roleName as Role;
    return ROLE_DISPLAY_NAMES[role] || roleName;
  }

  /**
   * 获取角色描述
   */
  static getRoleDescription(roleName: string): string {
    const role = roleName as Role;
    return ROLE_DESCRIPTIONS[role] || '';
  }

  /**
   * 格式化日期
   */
  static formatDate(date: Date): string {
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  /**
   * 获取角色状态文本
   */
  static getRoleStatusText(active: boolean): string {
    return active ? '启用' : '禁用';
  }

  /**
   * 检查是否为系统角色
   */
  static isSystemRole(roleName: string): boolean {
    return Object.values(ROLES).includes(roleName as Role);
  }

  /**
   * 获取角色标签颜色
   */
  static getRoleTagColor(roleName: string): string {
    const role = roleName as Role;
    const colorMap: Record<Role, string> = {
      [ROLES.SUPER_ADMIN]: 'red',
      [ROLES.ADMIN]: 'orange',
      [ROLES.MANAGER]: 'blue',
      [ROLES.EMPLOYEE]: 'green'
    };
    return colorMap[role] || 'default';
  }
}